import type { Crop } from 'react-image-crop';
import type { GridChildComponentProps } from '@shared/components/VirtualScroll';
import type { WebsitePhotoAlignment } from '@shared/types/WebsiteHeaderSettings.types';
import type { MediaPhoto as MediaPhotoQueryResult } from '@apps/admin/components/PhotoUploader/PhotoUploader.types';

export enum PICKER_TYPES {
  ADD_TO_COLLECTION = 'ADD_TO_COLLECTION',
  PICK_FROM_ALL_PHOTOS = 'PICK_FROM_ALL_PHOTOS',
}

export type PhotoCropData = Readonly<{
  photoId: string;
  aspectRatio: number | 'locked' | null;
  crop?: Crop;
  alignment?: WebsitePhotoAlignment;
  mobileAspectRatio?: number;
  mobileCrop?: Crop;
  url: string;
  height: number;
  width: number;
}>;

export type AddMediaArgs = Readonly<{
  selectedItemsId: ReadonlyArray<string>;
  cropData: Crop | null;
  alignmentData: WebsitePhotoAlignment | null;
  mobileCropData: Crop | null;
  cb: () => void;
}>;

export type PhotoPickerDialogProps = Readonly<{
  isOpen: boolean;
  handleClose: () => void;
  handleOpenFilePicker: () => void;
  isFetching: boolean;
  media?: ReadonlyArray<MediaPhotoQueryResult> | null;
  handleAddMedia: (args: AddMediaArgs) => void;
  isAddingMedia: boolean;
  collectionId?: string;
  showCount?: boolean;
  pickerType?: PICKER_TYPES;
  maxCount?: number;
  minCount?: number;
  photoSelectionHelperText?: string;
  selectedItems?: ReadonlyArray<string>;
  disabledItems?: ReadonlyArray<string>;
  uploadInProgressCount?: number;
  supportsPhotoCrop?: boolean;
  photoCropData?: PhotoCropData;
  defaultTab?: TabType;
  enableMobileCrop?: boolean;
  customHeaderContent?: React.ReactNode;
  primaryCTALabel?: string;
  dialogTitle?: string;
  disableAutoCloseOnFilePicker?: boolean;
  onSelectionChange?: (selectedItemsId: ReadonlyArray<string>) => void;
  showBulkActionCTAs?: boolean;
  allowEmptySelection?: boolean;
}>;

export type PhotoPickerLibraryProps = Readonly<{
  isFetching: boolean;
  media?: ReadonlyArray<MediaPhotoQueryResult> | null;
  selectedItemsId: ReadonlyArray<string>;
  disabledItems: ReadonlyArray<string>;
  updateSelectedItemsId: (newSelectedItemId: string) => void;
  handleOpenFilePicker: () => void;
  showCount?: boolean;
  photoSelectionHelperText?: string;
  uploadInProgressCount?: number;
}>;

export type DialogFooterProps = Readonly<{
  handleSelectAll?: () => void;
  handleDeselect?: () => void;
  handleCancel: () => void;
  handleAdd: () => void;
  isAdding: boolean;
  disableAddCTA: boolean;
  disableSelectAllCTA?: boolean;
  disableDeselectAllCTA?: boolean;
  showBulkActionCTAs?: boolean;
  pickerType?: PICKER_TYPES;
  selectedItemsCount?: number;
  supportsPhotoCrop?: boolean;
  selectedTab?: TabType;
  enableMobileCrop?: boolean;
  primaryCTALabel?: string;
}>;

export type MediaPhoto = Readonly<{
  id: string;
  photo: {
    id: string;
    url: string;
    height: number;
    width: number;
  };
}>;

export type GridItemProps = GridChildComponentProps<{
  mediaArray: ReadonlyArray<MediaPhoto>;
  updateSelectedItemsId: (newSelectedItemId: string) => void;
  selectedItemsId: ReadonlyArray<string>;
  disabledItems: ReadonlyArray<string>;
  showCount: boolean;
}>;

export enum TabType {
  SELECT_PHOTO = 'selectPhoto',
  CROP = 'crop',
  MOBILE_CROP = 'mobileCrop',
}

export type PhotoDisplayConfig = Readonly<{
  crop: Crop | undefined;
  focusPoint: WebsitePhotoAlignment | undefined;
  mobileCrop: Crop | undefined;
}>;

export type DialogHeaderProps = Readonly<{
  handleClose: () => void;
  dialogTitle: string;
}>;

export type TabNavigationProps = Readonly<{
  selectedTab: TabType;
  onSelectTab: (tab: TabType) => void;
  isCropTabDisabled: boolean;
  enableMobileCrop?: boolean;
  isMobileCropTabDisabled?: boolean;
}>;

export interface PhotoCardSkeletonGroupProps
  extends Readonly<{
    loading: boolean;
    count?: number;
    height?: number;
  }> {}
